import { PAN_REFERENCE_M } from '../config/audio-tuning';

export interface Vec2 {
  x: number;
  z: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Manual stereo-pan + linear distance falloff for a world-space sound
 * source relative to the listener (camera) — simpler than a full 3D
 * PannerNode/HRTF graph, sufficient for this game's fixed over-the-shoulder
 * camera. Rotation matches `OverShoulderCameraController.worldDirection()`'s
 * own convention (local forward (0,1) -> world (sin(yaw), cos(yaw)), local
 * right (1,0) -> world (-cos(yaw), sin(yaw))) so a sound's pan direction
 * agrees with what the camera is actually showing, not an independently
 * invented convention.
 */
export function computeSpatialParams(
  listener: Vec2,
  listenerYawRad: number,
  source: Vec2,
  maxDistanceM: number,
): { pan: number; gain01: number } {
  const dx = source.x - listener.x;
  const dz = source.z - listener.z;
  const distance = Math.hypot(dx, dz);
  if (distance < 1e-6) return { pan: 0, gain01: 1 };

  const localRight = -dx * Math.cos(listenerYawRad) + dz * Math.sin(listenerYawRad);
  const pan = clamp(localRight / PAN_REFERENCE_M, -1, 1);
  const gain01 = clamp(1 - distance / maxDistanceM, 0, 1);
  return { pan, gain01 };
}
